import { useState } from "react";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-200/60 shadow-sm">
        <div className="w-full px-4 md:px-8 py-3 flex items-center justify-between">
          {/* Brand */}
          <a href="/" className="flex flex-col leading-none no-underline shrink-0">
            <span className="text-sm font-bold tracking-widest uppercase text-foreground-900 font-body">HIPOBUY</span>
            <span className="text-xs font-light tracking-widest uppercase text-foreground-700 font-body">SPREADSHEET</span>
          </a>

          {/* Desktop Nav Items */}
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="px-4 py-2 rounded-full text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer font-body"
            >
              Accueil
            </button>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200/60 text-sm text-foreground-900 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer font-body"
              >
                <span>Catégories</span>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </span>
              </button>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-background-200/60 shadow-lg overflow-hidden z-50 max-h-[60vh] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-primary-600 transition-colors border-b border-background-100/50 last:border-0 font-body whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate("/guide-achat-produits")}
              className="px-4 py-2 rounded-full text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer font-body"
            >
              Guide d'Achat
            </button>
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer font-body"
            >
              <span>Hipobuy Spreadsheet</span>
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer font-body"
            >
              <span>Buy It Now</span>
              <i className="ri-arrow-right-line" />
            </a>

            {/* Mobile Menu Toggle */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/60 cursor-pointer"
            >
              <i className="ri-menu-line text-xl text-foreground-900" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 right-0 bottom-0 w-[85vw] max-w-[380px] bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200/60">
              <span className="text-sm font-bold tracking-widest uppercase text-foreground-900 font-body">Menu</span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl text-foreground-900" />
              </button>
            </div>

            <div className="p-4 space-y-1">
              <button
                type="button"
                onClick={() => { navigate("/"); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer font-body"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-home-4-line" />
                </span>
                Accueil
              </button>

              <button
                type="button"
                onClick={() => { navigate("/guide-achat-produits"); setMobileMenuOpen(false); }}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-semibold text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer font-body"
              >
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-shopping-bag-3-line" />
                </span>
                Guide d'Achat
              </button>
            </div>

            {/* Categories in Mobile Menu */}
            <div className="px-4 py-2">
              <p className="text-xs font-bold uppercase tracking-wider text-foreground-400 px-4 py-2 font-body">Catégories</p>
              <div className="space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-body"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Mobile SEO Articles */}
            <div className="px-4 py-2 mt-2 border-t border-background-100">
              <p className="text-xs font-bold uppercase tracking-wider text-foreground-400 px-4 py-2 font-body">Articles SEO</p>
              <div className="space-y-0.5">
                {[
                  { label: "Débuter avec Hipobuy", slug: "/hipobuy-debutant" },
                  { label: "Hipobuy vs Google Sheets", slug: "/hipobuy-vs-google-sheets" },
                  { label: "Top 10 Templates", slug: "/top-10-templates" },
                  { label: "Erreurs à éviter", slug: "/erreurs-a-eviter" },
                  { label: "Guide débutant complet", slug: "/guide-debutant-complet" },
                  { label: "Automatisation avancée", slug: "/automatisation-avancee" },
                  { label: "Sécurité & confidentialité", slug: "/securite-confidentialite" },
                  { label: "Optimiser workflow", slug: "/optimiser-workflow" },
                  { label: "Guide d'achat produits", slug: "/guide-achat-produits" },
                ].map((article) => (
                  <button
                    key={article.slug}
                    type="button"
                    onClick={() => { navigate(article.slug); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-body"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-article-line" />
                    </span>
                    {article.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}